/**
 * KnowledgeSynapseDetail — 2026-07-02.
 *
 * THE SYNAPSE detail panel — the knowledge LOOP, not a corpus. The mind-chat
 * wiki compiler turns MIND/CORTEX knowledge into per-tenant wiki articles
 * (3 AM cron on T440, or the manual trigger here). Loop state comes from
 * `GET /api/knowledge/synapse/status` (engine probe + the wiki index.md the
 * compiler rewrites each run); "never compiled" is a calm zero, not an error.
 *
 * Follows the web_search precedent (1.185.0 Phase 7.2) for a non-corpus
 * Knowledge-tab surface: no /overview entry, no search, no ingest target.
 */
import clsx from 'clsx';
import { Loader2, Zap } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { KnowledgeSynapseLoopView } from './KnowledgeSynapseLoopView';

export function KnowledgeSynapseDetail() {
  const status = useKnowledgeStore((s) => s.synapseStatus);
  const isFetching = useKnowledgeStore((s) => s.isFetchingSynapseStatus);
  const statusError = useKnowledgeStore((s) => s.synapseStatusError);
  const isCompiling = useKnowledgeStore((s) => s.isSynapseCompiling);
  const compileResult = useKnowledgeStore((s) => s.synapseCompileResult);
  const compileError = useKnowledgeStore((s) => s.synapseCompileError);
  const runCompile = useKnowledgeStore((s) => s.runSynapseCompile);
  const fetchStatus = useKnowledgeStore((s) => s.fetchSynapseStatus);

  const available = status?.available ?? false;
  // Status-leg error (engine/wiki down) OR compile-leg error — one banner,
  // honest text from whichever leg failed.
  const errorText = compileError ?? status?.error ?? statusError?.message ?? null;

  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="knowledge-synapse-detail">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-vibe-text flex items-center gap-2">
          <Zap className="w-5 h-5 text-vibe-svc-synapse" />
          THE SYNAPSE
        </h1>
        <span
          data-testid="knowledge-synapse-badge"
          className={clsx(
            'px-2 py-0.5 text-xs rounded',
            available
              ? 'bg-vibe-success/20 text-vibe-success'
              : 'bg-vibe-text-secondary/20 text-vibe-text-secondary',
          )}
        >
          {available ? 'Available' : 'Unavailable'}
        </span>
      </header>

      <div
        data-testid="knowledge-synapse-banner"
        className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 text-sm text-vibe-text-secondary mb-4"
      >
        The knowledge loop — compiles this tenant&apos;s MIND/CORTEX knowledge
        into wiki articles (nightly 3 AM cron, or compile on demand below).
        Compiled articles live in the Tenant Wiki and are searchable via
        OBSIDIAN and MIND.
      </div>

      {errorText && (
        <div
          data-testid="knowledge-synapse-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-3 text-sm text-vibe-warning mb-4"
        >
          {errorText}
        </div>
      )}

      {/* WS-A2 — knowledge-loop diagram (additive; reads the same store
          state as the cards below). */}
      <KnowledgeSynapseLoopView />

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded border border-vibe-border bg-vibe-bg-secondary p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-1">
            Compiled articles
          </div>
          <div
            data-testid="knowledge-synapse-article-count"
            className="text-3xl font-semibold text-vibe-text tabular-nums"
          >
            {status?.article_count == null ? '—' : status.article_count}
          </div>
        </div>
        <div className="rounded border border-vibe-border bg-vibe-bg-secondary p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-1">
            Last compiled
          </div>
          <div
            data-testid="knowledge-synapse-last-compiled"
            className="text-sm text-vibe-text mt-2 break-all"
          >
            {status?.last_compiled_at ?? 'Never compiled yet'}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <button
          data-testid="knowledge-synapse-compile"
          type="button"
          disabled={!available || isCompiling}
          onClick={() => void runCompile()}
          className="px-3 py-1.5 text-sm rounded bg-vibe-accent text-vibe-bg disabled:opacity-50"
        >
          {isCompiling ? 'Compiling…' : 'Compile now'}
        </button>
        <button
          data-testid="knowledge-synapse-refresh"
          type="button"
          disabled={isFetching}
          onClick={() => void fetchStatus()}
          className="px-3 py-1.5 text-xs rounded bg-vibe-bg-tertiary text-vibe-text-secondary hover:bg-vibe-border"
        >
          Refresh status
        </button>
        {isCompiling && (
          <span
            data-testid="knowledge-synapse-compiling"
            className="flex items-center gap-2 text-sm text-vibe-text-secondary"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Compiling — LLM synthesis can take a few minutes…
          </span>
        )}
      </div>

      {compileResult?.ok && (
        <div
          data-testid="knowledge-synapse-compile-result"
          className="rounded border border-vibe-success/30 bg-vibe-success/10 p-3 text-sm text-vibe-success mb-6"
        >
          Compile finished — {compileResult.articles.length} article
          {compileResult.articles.length === 1 ? '' : 's'} written
          {compileResult.articles.length > 0
            ? `: ${compileResult.articles.join(', ')}`
            : ''}
          {compileResult.errors.length > 0
            ? ` (${compileResult.errors.length} topic error${compileResult.errors.length === 1 ? '' : 's'})`
            : ''}
        </div>
      )}

      {status && status.articles.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-2">
            Article inventory
            {status.article_count != null &&
              status.article_count > status.articles.length &&
              ` (showing ${status.articles.length} of ${status.article_count})`}
          </h2>
          <ul className="space-y-1">
            {status.articles.map((slug, i) => (
              <li
                key={slug}
                data-testid={`knowledge-synapse-article-${i}`}
                className="flex items-center gap-2 border-b border-vibe-border py-1.5 text-sm"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-vibe-svc-synapse flex-shrink-0" />
                <span className="font-mono text-vibe-text truncate">{slug}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {status && status.wiki_available && status.articles.length === 0 && (
        <div
          data-testid="knowledge-synapse-empty"
          className="text-sm text-vibe-text-secondary py-2"
        >
          No compiled articles yet — run Compile now (or wait for the nightly
          cron) to build this tenant&apos;s wiki from its knowledge.
        </div>
      )}
    </div>
  );
}
